import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { tokenService } from '../lib/tokenService';
import { subscriptionService } from '../lib/subscriptionService';
import { PaymentHistory } from './PaymentHistory';
import { CompanyCheckoutForm } from './CompanyCheckoutForm';
import { TokenPackage } from '../types/auth';

interface BuyTokensProps {
  onClose: () => void;
}

export const BuyTokens: React.FC<BuyTokensProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<TokenPackage | null>(null);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);

  useEffect(() => {
    console.log('🔧 BuyTokens component mounted');
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      const data = await tokenService.getTokenPackages();
      setPackages(data);
    } catch (err: any) {
      setError('Failed to load token packages');
      console.error('Error loading packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseClick = async (pkg: TokenPackage) => {
    if (!user?.id) return;
    
    console.log('🛒 Purchase clicked for package:', pkg.name);
    
    // Check if this is a subscription package
    if (pkg.is_subscription) {
      try {
        const successUrl = `${window.location.origin}/payment-success`;
        const cancelUrl = `${window.location.origin}/dashboard`;
        
        const { url } = await subscriptionService.createSubscriptionCheckout(
          pkg.id,
          user.id,
          successUrl,
          cancelUrl
        );
        
        // Redirect to Stripe checkout
        window.location.href = url;
      } catch (err: any) {
        setError('Failed to create subscription checkout: ' + err.message);
        console.error('Subscription checkout error:', err);
      }
    } else {
      // Handle one-time purchase
      setSelectedPackage(pkg);
      setShowCompanyForm(true);
      setError(null);
    }
  };



  const handleCompanyFormClose = () => {
    setShowCompanyForm(false);
    setSelectedPackage(null);
    setError(null);
  };

  const getMostPopular = () => {
    // Return the package with the best tokens per dollar ratio
    if (packages.length === 0) return null;
    return packages.reduce((best, current) => {
      const bestRatio = tokenService.getTokensPerDollar(best.tokens, best.price_cents);
      const currentRatio = tokenService.getTokensPerDollar(current.tokens, current.price_cents);
      return currentRatio > bestRatio ? current : best;
    });
  };

  const mostPopular = getMostPopular();

  // Show company checkout form
  if (showCompanyForm && selectedPackage && user) {
    return (
      <>
        <CompanyCheckoutForm
          tokenPackage={selectedPackage}
          userId={user.id}
          onClose={handleCompanyFormClose}
          onError={setError}
        />

        {/* Payment History Modal */}
        {showPaymentHistory && (
          <PaymentHistory onClose={() => setShowPaymentHistory(false)} />
        )}
      </>
    );
  }

  // Show package selection
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Buy Tokens</h2>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowPaymentHistory(true)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Purchase History
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <div className="mb-6">
            <p className="text-gray-600">
              Tokens are used to generate AI-powered renovation images and designs. 
              Choose a package that fits your needs:
            </p>
          </div>



          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`relative border rounded-lg p-6 ${
                    pkg.id === mostPopular?.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  {pkg.id === mostPopular?.id && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        BEST VALUE
                      </span>
                    </div>
                  )}

                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {pkg.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {pkg.description}
                    </p>

                    <div className="mb-4">
                      <div className="text-3xl font-bold text-gray-900">
                        {pkg.tokens}
                      </div>
                      <div className="text-sm text-gray-500">tokens</div>
                    </div>

                    <div className="mb-4">
                      <div className="text-xl font-semibold text-gray-900">
                        {pkg.is_subscription 
                          ? subscriptionService.formatSubscriptionPrice(
                              pkg.price_cents, 
                              pkg.subscription_interval || 'month', 
                              pkg.subscription_interval_count || 1
                            )
                          : `$${tokenService.formatPrice(pkg.price_cents)}`
                        }
                      </div>
                      <div className="text-xs text-gray-500">
                        {pkg.is_subscription 
                          ? `${pkg.tokens} tokens per ${pkg.subscription_interval || 'month'}`
                          : `${tokenService.getTokensPerDollar(pkg.tokens, pkg.price_cents).toFixed(1)} tokens per $1`
                        }
                      </div>
                      {pkg.is_subscription && pkg.subscription_interval === 'year' && (
                        <div className="text-xs text-green-600 font-medium mt-1">
                          Save {subscriptionService.calculateYearlySavings(
                            pkg.price_cents * 12, 
                            pkg.price_cents
                          )}% vs monthly
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => handlePurchaseClick(pkg)}
                      className={`w-full py-2 px-4 rounded-md font-medium transition-colors ${
                        pkg.id === mostPopular?.id
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-600 hover:bg-gray-700 text-white'
                      }`}
                    >
                      {pkg.is_subscription ? 'Subscribe Now' : 'Purchase with Stripe'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">How Tokens Work</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Generate AI renovation images: ~5-10 tokens per image</li>
              <li>• Get design suggestions: ~2-5 tokens per request</li>
              <li>• Tokens never expire and can be used anytime</li>
              <li>• Secure payments processed by Stripe</li>
            </ul>
          </div>

          <div className="mt-6 text-center">
            <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Secure SSL Payment
              </div>
              <div className="flex items-center">
                <svg className="h-4 w-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Powered by Stripe
              </div>
              <div className="flex items-center">
                <svg className="h-4 w-4 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Instant Token Delivery
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Payment History Modal */}
    {showPaymentHistory && (
      <PaymentHistory onClose={() => setShowPaymentHistory(false)} />
    )}
    </>
  );
}; 